// What the error screen keeps about a crash.
//
// "Something went wrong on this page" told the agent nothing and kept nothing: the cause went to
// the browser console and was gone with the tab (VT review 3/8, G: a reviewer hit it on his first
// search and it could not be traced afterwards). The screen now keeps a short record in the
// browser's session storage and shows a reference with the details ready to copy, so the next
// report can be read. Nothing is sent anywhere.

export const ERROR_RECORD_KEY = "vt-last-page-error";

const firstLines = (text, n) =>
  String(text || "")
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean)
    .slice(0, n);

// A short, stable tag for one message: the same crash gives the same four characters.
const tagOf = (text) => {
  let h = 0;
  for (const ch of String(text)) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return h.toString(16).toUpperCase().padStart(8, "0").slice(-4);
};

/**
 * @param {Error|*} error      what was thrown
 * @param {object}  [info]     React's error info ({ componentStack })
 * @param {object}  [where]    { path, now } - the page and the moment
 */
export function buildErrorRecord(error, info, { path = "", now = new Date() } = {}) {
  const message = String((error && error.message) || error || "Unknown error").slice(0, 500);
  const at = now.toISOString();
  return {
    ref: `E-${at.slice(0, 10).replace(/-/g, "")}-${at.slice(11, 16).replace(":", "")}-${tagOf(message)}`,
    at,
    path: String(path || ""),
    name: String((error && error.name) || "Error"),
    message,
    stack: firstLines(error && error.stack, 8),
    component: firstLines(info && info.componentStack, 6),
  };
}

/** Keeps the record for this browser tab. Never throws: a full or blocked store is ignored. */
export function saveErrorRecord(record, storage) {
  try {
    (storage || window.sessionStorage).setItem(ERROR_RECORD_KEY, JSON.stringify(record));
    return true;
  } catch (e) {
    return false;
  }
}

export function readErrorRecord(storage) {
  try {
    const raw = (storage || window.sessionStorage).getItem(ERROR_RECORD_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
}

/** The record as plain text, for the "Copy details" link. */
export function errorRecordText(record) {
  if (!record) return "";
  return [
    `Reference: ${record.ref}`,
    `Time: ${record.at}`,
    `Page: ${record.path}`,
    `Error: ${record.name}: ${record.message}`,
    ...(record.stack && record.stack.length ? ["Stack:", ...record.stack.map((l) => `  ${l}`)] : []),
    ...(record.component && record.component.length ? ["In:", ...record.component.map((l) => `  ${l}`)] : []),
  ].join("\n");
}
